/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Typography } from '@mui/material';
import React from 'react';
import { SwitchButton } from '@opengeoweb/shared';
import {
  TIME_SERIES_CONTAINER_BREAKPOINT_PX,
  TIME_SERIES_CONTAINER_NAME,
} from './constants';
import { useTimeseriesTranslation } from '../../utils/i18n';

export interface TimeSeriesMarkersSwitchProps {
  shouldHideMarkers: boolean;
  toggleMarkers: (shouldHideMarkers: boolean) => void;
}

export const TimeSeriesMarkersSwitch: React.FC<
  TimeSeriesMarkersSwitchProps
> = ({ shouldHideMarkers, toggleMarkers }) => {
  const { t } = useTimeseriesTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        'label:first-of-type': {
          marginLeft: '5px',
          marginRight: '-5px',
        },
        'label:last-of-type': {
          marginLeft: '-5px',
        },
        '.switch-button label': {
          display: 'none',
        },
        '.markers-label': {
          display: 'none',
        },
        [`@container ${TIME_SERIES_CONTAINER_NAME} (min-width: ${TIME_SERIES_CONTAINER_BREAKPOINT_PX})`]:
          {
            minWidth: '146px',
            '.markers-label, .switch-button label': {
              display: 'initial',
            },
          },
      }}
    >
      <Typography
        className="markers-label"
        sx={{ marginTop: '4px' }}
        variant="body2"
      >
        {t('timeseries-toggle-location-markers-label')}
      </Typography>
      <Box sx={{ marginTop: '-5px' }}>
        <SwitchButton
          activeLabel="ON"
          checked={!shouldHideMarkers}
          inActiveLabel="OFF"
          onChange={(event) => {
            toggleMarkers(!event.target.checked);
          }}
          tooltip={t('timeseries-toggle-location-markers-tooltip')}
        />
      </Box>
    </Box>
  );
};
